
import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Bell, Search, Menu, X, PenSquare, Settings, LogOut, User, Shield, ChevronDown, BookOpen } from 'lucide-react';

const Navbar = () => {
  const { user, logout, unreadCount, notifications, markNotificationsRead } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const userMenuRef = useRef(null);
  const notifRef = useRef(null);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false);
      if (notifRef.current && !notifRef.current.contains(e.target)) setNotifOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Close mobile menu on route change
  useEffect(() => { setMenuOpen(false); }, [location]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchQuery('');
      setSearchOpen(false);
    }
  };

  const avatarFallback = user?.name?.charAt(0).toUpperCase();

  return (
    <nav style={{
      position: 'sticky', top: 0, zIndex: 100,
      background: 'rgba(10, 10, 22, 0.92)',
      backdropFilter: 'blur(20px)',
      borderBottom: '1px solid rgba(255,255,255,0.06)',
      padding: '0 24px',
      height: '64px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    }}>
      {/* Logo */}
      <Link to="/" style={{ display: 'flex', alignItems: 'center', gap: '10px', textDecoration: 'none' }}>
        <div style={{
          width: '36px', height: '36px', borderRadius: '10px',
          background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 0 20px rgba(139, 92, 246, 0.4)'
        }}>
          <BookOpen size={18} color="white" />
        </div>
        <span style={{ fontFamily: 'var(--font-display)', fontSize: '20px', fontWeight: '700', color: 'var(--text-primary)' }}>
          Wom<span style={{ color: 'var(--accent-light)' }}>Lus</span>
        </span>
      </Link>

      {/* Desktop Nav */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }} className="desktop-nav">
        <NavLink to="/blog">Blog</NavLink>
        <NavLink to="/category/technology">Tech</NavLink>
        <NavLink to="/category/lifestyle">Lifestyle</NavLink>

        {/* Search */}
        {searchOpen ? (
          <form onSubmit={handleSearch} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <input
              autoFocus
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search posts..."
              style={{
                padding: '8px 14px', background: 'var(--bg-elevated)',
                border: '1px solid var(--accent-border)', borderRadius: '8px',
                color: 'var(--text-primary)', outline: 'none', width: '200px',
                fontFamily: 'var(--font-body)', fontSize: '14px'
              }}
            />
<button type="button" onClick={() => setSearchOpen(false)} aria-label="Close search" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)' }}>
              <X size={18} />
            </button>
          </form>
        ) : (
<button onClick={() => setSearchOpen(true)} aria-label="Open search" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: '8px', borderRadius: '8px', display: 'flex' }}
className="icon-btn">
            <Search size={18} />
          </button>
        )}

        {user ? (
          <>
            {/* Write Button */}
            <Link to="/write" className="btn btn-primary btn-sm">
              <PenSquare size={15} /> Write
            </Link>

            {/* Notifications */}
            <div ref={notifRef} style={{ position: 'relative' }}>
<button onClick={() => { setNotifOpen(!notifOpen); if (!notifOpen) markNotificationsRead(); }} aria-label="Notifications"                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-secondary)', padding: '8px', borderRadius: '8px', display: 'flex', position: 'relative' }}>
                <Bell size={18} />
                {unreadCount > 0 && (
                  <span style={{
                    position: 'absolute', top: '4px', right: '4px',
                    width: '16px', height: '16px', borderRadius: '50%',
                    background: 'var(--accent)', color: 'white',
                    fontSize: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontWeight: '700'
                  }}>{unreadCount > 9 ? '9+' : unreadCount}</span>
                )}
              </button>
              {notifOpen && (
                <div style={{
                  position: 'absolute', right: 0, top: '48px', width: '320px',
                  background: 'var(--bg-card)', border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow)', overflow: 'hidden', zIndex: 200
                }}>
                  <div style={{ padding: '16px', borderBottom: '1px solid var(--border)', fontWeight: '600', fontSize: '15px' }}>
                    Notifications
                  </div>
                  <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {notifications.length === 0 ? (
                      <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text-muted)', fontSize: '14px' }}>No notifications yet</div>
                    ) : notifications.map((n, i) => (
                      <div key={i} onClick={() => navigate(n.link || '/')} style={{
                        padding: '14px 16px', borderBottom: '1px solid var(--border)',
                        cursor: 'pointer', background: n.read ? 'transparent' : 'rgba(139, 92, 246, 0.05)',
                        fontSize: '14px', color: 'var(--text-secondary)'
                      }} onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-elevated)'}
                        onMouseLeave={e => e.currentTarget.style.background = n.read ? 'transparent' : 'rgba(139, 92, 246, 0.05)'}>
                        {n.message}
                        <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '4px' }}>
                          {new Date(n.createdAt).toLocaleDateString()}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* User Menu */}
            <div ref={userMenuRef} style={{ position: 'relative' }}>
              <button onClick={() => setUserMenuOpen(!userMenuOpen)} style={{
                display: 'flex', alignItems: 'center', gap: '8px',
                background: 'none', border: 'none', cursor: 'pointer', padding: '4px 8px', borderRadius: '8px'
              }}>
                {user.avatar ? (
                  <img src={user.avatar} alt={user.name} className="avatar" style={{ width: '32px', height: '32px' }} />
                ) : (
                  <div className="avatar-fallback" style={{ width: '32px', height: '32px', fontSize: '14px' }}>{avatarFallback}</div>
                )}
                <span style={{ fontSize: '14px', color: 'var(--text-primary)', fontWeight: '500' }}>{user.name.split(' ')[0]}</span>
                <ChevronDown size={14} color="var(--text-muted)" style={{ transform: userMenuOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
              </button>
              {userMenuOpen && (
                <div style={{
                  position: 'absolute', right: 0, top: '48px', width: '200px',
                  background: 'var(--bg-card)', border: '1px solid var(--border)',
                  borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow)', overflow: 'hidden', zIndex: 200
                }}>
                  <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border)' }}>
                    <div style={{ fontWeight: '600', fontSize: '14px', color: 'var(--text-primary)' }}>{user.name}</div>
                    <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>{user.email}</div>
                    {user.role !== 'user' && <span className={`badge badge-${user.role === 'admin' ? 'accent' : 'info'}`} style={{ marginTop: '6px', fontSize: '11px' }}>{user.role}</span>}
                  </div>
                  <UserMenuItem icon={User} label="Profile" onClick={() => { navigate(`/profile/${user._id}`); setUserMenuOpen(false); }} />
                  <UserMenuItem icon={PenSquare} label="Dashboard" onClick={() => { navigate('/dashboard'); setUserMenuOpen(false); }} />
                  {user.role === 'admin' && <UserMenuItem icon={Shield} label="Admin Panel" onClick={() => { navigate('/admin'); setUserMenuOpen(false); }} />}
                  <div style={{ borderTop: '1px solid var(--border)' }}>
                    <UserMenuItem icon={LogOut} label="Logout" onClick={() => { logout(); setUserMenuOpen(false); }} danger />
                  </div>
                </div>
              )}
            </div>
          </>
        ) : (
          <div style={{ display: 'flex', gap: '8px' }}>
            <Link to="/login" className="btn btn-ghost btn-sm">Sign In</Link>
            <Link to="/register" className="btn btn-primary btn-sm">Get Started</Link>
          </div>
        )}
      </div>

      {/* Mobile Menu Button */}
<button onClick={() => setMenuOpen(!menuOpen)} aria-label="Toggle menu" style={{        display: 'none', background: 'none', border: 'none',
        cursor: 'pointer', color: 'var(--text-primary)', padding: '8px'
      }} className="mobile-menu-btn">
        {menuOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {/* Mobile Menu */}
      {menuOpen && (
        <div style={{
          position: 'fixed', inset: '64px 0 0 0',
          background: 'var(--bg-primary)', zIndex: 99, padding: '24px',
          display: 'flex', flexDirection: 'column', gap: '8px'
        }} className="mobile-menu">
          <MobileNavLink to="/blog" onClick={() => setMenuOpen(false)}>Blog</MobileNavLink>
          <MobileNavLink to="/category/technology" onClick={() => setMenuOpen(false)}>Technology</MobileNavLink>
          <MobileNavLink to="/category/lifestyle" onClick={() => setMenuOpen(false)}>Lifestyle</MobileNavLink>
          {user ? (
            <>
              <MobileNavLink to="/write" onClick={() => setMenuOpen(false)}>Write Post</MobileNavLink>
              <MobileNavLink to="/dashboard" onClick={() => setMenuOpen(false)}>Dashboard</MobileNavLink>
              {user.role === 'admin' && <MobileNavLink to="/admin" onClick={() => setMenuOpen(false)}>Admin Panel</MobileNavLink>}
              <button onClick={() => { logout(); setMenuOpen(false); }}
                style={{ padding: '12px 16px', background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 'var(--radius)', color: 'var(--danger)', cursor: 'pointer', fontFamily: 'var(--font-body)', fontSize: '15px', textAlign: 'left', marginTop: '8px' }}>
                Sign Out
              </button>
            </>
          ) : (
            <>
              <Link to="/login" className="btn btn-ghost" onClick={() => setMenuOpen(false)}>Sign In</Link>
              <Link to="/register" className="btn btn-primary" onClick={() => setMenuOpen(false)}>Get Started</Link>
            </>
          )}
        </div>
      )}

      <style>{`
        @media (max-width: 768px) {
          .desktop-nav { display: none !important; }
          .mobile-menu-btn { display: flex !important; }
        }
        .icon-btn:hover { background: var(--bg-elevated) !important; color: var(--text-primary) !important; }
      `}</style>
    </nav>
  );
};

const NavLink = ({ to, children }) => {
  const location = useLocation();
  const isActive = location.pathname === to;
  return (
    <Link to={to} style={{
      padding: '8px 14px', borderRadius: '8px', fontSize: '14px', fontWeight: '500',
      color: isActive ? 'var(--accent-light)' : 'var(--text-secondary)',
      textDecoration: 'none', transition: 'all 0.2s',
      background: isActive ? 'var(--accent-dim)' : 'transparent'
    }}
      onMouseEnter={e => { if (!isActive) e.target.style.color = 'var(--text-primary)'; e.target.style.background = 'var(--bg-elevated)'; }}
      onMouseLeave={e => { e.target.style.color = isActive ? 'var(--accent-light)' : 'var(--text-secondary)'; e.target.style.background = isActive ? 'var(--accent-dim)' : 'transparent'; }}>
      {children}
    </Link>
  );
};

const MobileNavLink = ({ to, children, onClick }) => (
  <Link to={to} onClick={onClick} style={{
    display: 'block', padding: '12px 16px', borderRadius: 'var(--radius)',
    fontSize: '15px', color: 'var(--text-primary)', textDecoration: 'none',
    background: 'var(--bg-card)', border: '1px solid var(--border)'
  }}>
    {children}
  </Link>
);

const UserMenuItem = ({ icon: Icon, label, onClick, danger }) => (
  <button onClick={onClick} style={{
    display: 'flex', alignItems: 'center', gap: '10px', width: '100%',
    padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer',
    fontSize: '14px', color: danger ? 'var(--danger)' : 'var(--text-secondary)',
    fontFamily: 'var(--font-body)', textAlign: 'left', transition: 'all 0.15s'
  }}
    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-elevated)'}
    onMouseLeave={e => e.currentTarget.style.background = 'none'}>
    <Icon size={15} />
    {label}
  </button>
);

export default Navbar;
